import { convert, money, type ExchangeRate } from '@fd/money';
import type { BalancePart } from './ownership.ts';
import { LIABILITY_TYPES, type LedgerAccount } from './types.ts';

export type RateLookup = (from: string, to: string) => ExchangeRate | null;

export type AccountValue = {
  readonly accountId: string;
  readonly baseMinor: bigint;
  readonly isLiability: boolean;
};

export type NetWorth = {
  readonly currency: string;
  readonly assetsMinor: bigint;
  /** Positive amount owed. */
  readonly liabilitiesMinor: bigint;
  readonly netMinor: bigint;
  readonly accounts: readonly AccountValue[];
  /** Accounts left out because no rate to the base currency is known. */
  readonly missingRates: readonly string[];
};

export function toBase(
  amountMinor: bigint,
  currency: string,
  baseCurrency: string,
  rates: RateLookup,
): bigint | null {
  if (currency === baseCurrency) return amountMinor;
  const rate = rates(currency, baseCurrency);
  if (!rate) return null;
  return convert(money(amountMinor, currency), rate, baseCurrency).minor;
}

/**
 * Net worth = assets − liabilities, in base currency. Liability balances are negative
 * (money owed); a card in credit reduces liabilities. Archived accounts and accounts
 * excluded by the user do not count. With `part`, only that part of each balance counts
 * (one person's share, ownership.ts); accounts it leaves out are skipped.
 */
export function computeNetWorth(
  accounts: readonly LedgerAccount[],
  baseCurrency: string,
  rates: RateLookup,
  part?: BalancePart,
): NetWorth {
  let assets = 0n;
  let liabilities = 0n;
  const values: AccountValue[] = [];
  const missing: string[] = [];
  for (const account of accounts) {
    if (account.archivedAt || !account.includeInNetWorth) continue;
    const balance = part ? part(account.id, account.balanceMinor) : account.balanceMinor;
    if (balance === null) continue;
    const value = toBase(balance, account.currency, baseCurrency, rates);
    if (value === null) {
      missing.push(account.id);
      continue;
    }
    const isLiability = LIABILITY_TYPES.has(account.type);
    if (isLiability) liabilities -= value;
    else assets += value;
    values.push({ accountId: account.id, baseMinor: value, isLiability });
  }
  return {
    currency: baseCurrency,
    assetsMinor: assets,
    liabilitiesMinor: liabilities,
    netMinor: assets - liabilities,
    accounts: values,
    missingRates: missing,
  };
}
